import { Spinner } from '../../../components/ui/Spinner';
import { useDropZone, useFileUpload, useUploader } from './hooks';
import { Button } from '../../../components/ui/Button';
import { Icon } from '../../../components/ui/Icon';
import { Box, Typography } from '@mui/material';
import { ChangeEvent, useCallback } from 'react';

export const ImageUploader = ({
  onUpload,
}: {
  onUpload: (url: string) => void;
}) => {
  const { loading, uploadFile } = useUploader({ onUpload });
  const { handleUploadClick, ref } = useFileUpload();
  const { draggedInside, onDrop, onDragEnter, onDragLeave } = useDropZone({
    uploader: uploadFile,
  });

  const onFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      e.target.files ? uploadFile(e.target.files[0]) : null,
    [uploadFile]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          borderRadius: 2,
          minHeight: '10rem',
          bgcolor: 'background.paper',
          opacity: 0.8,
        }}
      >
        <Spinner size={1.5} sx={{ color: 'text.secondary' }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 4,
        py: 5,
        borderRadius: 2,
        bgcolor: draggedInside ? 'action.hover' : 'background.paper',
        opacity: 0.8,
      }}
      onDrop={onDrop}
      onDragOver={onDragEnter}
      onDragLeave={onDragLeave}
      contentEditable={false}
    >
      <Icon
        name='Image'
        sx={{
          width: 48,
          height: 48,
          mb: 2,
          color: 'text.primary',
          opacity: 0.2,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontWeight: 500,
            textAlign: 'center',
            color: 'text.secondary',
          }}
        >
          {draggedInside ? 'Drop image here' : 'Drag and drop or'}
        </Typography>
        <Box>
          <Button
            disabled={draggedInside}
            onClick={handleUploadClick}
            variant='primary'
            buttonSize='small'
          >
            <Icon name='Upload' />
            Upload an image
          </Button>
        </Box>
      </Box>
      <Box
        component='input'
        sx={{
          width: 0,
          height: 0,
          overflow: 'hidden',
          opacity: 0,
        }}
        ref={ref}
        type='file'
        accept='.jpg,.jpeg,.png,.webp,.gif'
        onChange={onFileChange}
      />
    </Box>
  );
};

export default ImageUploader;
